import {
  currentAssistantMapContext,
  focusAssistantMap,
  notifyAssistantDataChanged,
  type AssistantMapContext,
} from './assistantMapContext';

export type AiChatMessage = {
  role: 'user' | 'assistant';
  content: string;
};

export type AiLocationContext = {
  latitude: number;
  longitude: number;
  accuracy: number | null;
  observed_at: string;
};

export type AiTripDayContext = {
  id: string;
  name: string;
  date: string | null;
};

export type AiReferencedEntity = {
  type: 'poi' | 'place' | 'stop';
  id?: string;
  name: string;
  category?: string;
  latitude: number;
  longitude: number;
  distance_m?: number;
  website?: string;
  operator_website?: string;
  opening_hours?: string;
  wikipedia?: string;
  wikidata?: string;
  cuisine?: string;
  diet_vegetarian?: string;
  diet_vegan?: string;
};

export type AiConversationState = {
  referencedEntity?: AiReferencedEntity;
  places?: AiReferencedEntity[];
};

export type AiUiAction = {
  type: 'focus_map';
  latitude: number;
  longitude: number;
  zoom?: number;
  name?: string;
  markerKind?: string;
};

export type AiChatResponse = {
  text: string;
  model: string;
  usage?: {
    promptTokens?: number;
    outputTokens?: number;
    totalTokens?: number;
  };
  quota?: {
    used: number;
    limit: number;
    remaining: number;
  };
  toolCalls?: Array<{ name: string; isError: boolean; mutated?: boolean }>;
  uiActions?: AiUiAction[];
  conversationState?: AiConversationState;
};

type AiErrorDiagnostic = {
  code?: string;
  stage?: string;
  retryable?: boolean;
  requestId?: string;
};

type AiErrorPayload = {
  error?: string;
  code?: string;
  stage?: string;
  retryable?: boolean;
  request_id?: string;
};

export class AiClientError extends Error {
  status: number;
  code?: string;
  stage?: string;
  retryable: boolean;
  requestId?: string;

  constructor(status: number, message: string, diagnostic: AiErrorDiagnostic = {}) {
    super(message);
    this.status = status;
    this.code = diagnostic.code;
    this.stage = diagnostic.stage;
    this.retryable = diagnostic.retryable ?? false;
    this.requestId = diagnostic.requestId;
  }
}

const DEFAULT_AI_URL = 'https://doddledays-ai-344520092879.europe-west4.run.app';
const MAX_HISTORY_MESSAGES = 16;
const MAX_MESSAGE_LENGTH = 2_000;
const MAX_TRIP_DAYS = 24;
const conversationStateByTrip = new Map<string, AiConversationState>();

export function aiServiceUrl(envUrl = import.meta.env.VITE_AI_URL as string | undefined): string {
  return (envUrl?.trim() || DEFAULT_AI_URL).replace(/\/$/, '');
}

export function chatMessages(history: AiChatMessage[], message: string): AiChatMessage[] {
  const clean = message.trim();
  if (!clean) throw new Error('message is required');
  if (clean.length > MAX_MESSAGE_LENGTH) throw new Error(`message is limited to ${MAX_MESSAGE_LENGTH} characters`);
  return [
    ...history
      .filter((item) => (item.role === 'user' || item.role === 'assistant') && item.content.trim())
      .slice(-MAX_HISTORY_MESSAGES),
    { role: 'user' as const, content: clean },
  ];
}

function calendarDate(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const match = value.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day, 12));
  if (date.getUTCFullYear() !== year || date.getUTCMonth() !== month - 1 || date.getUTCDate() !== day) return null;
  return `${match[1]}-${match[2]}-${match[3]}`;
}

function legacyRouteDate(route: Record<string, unknown>): string | null {
  const waypoints = Array.isArray(route.waypoints) ? route.waypoints : [];
  return waypoints
    .map((waypoint) => {
      if (!waypoint || typeof waypoint !== 'object' || Array.isArray(waypoint)) return null;
      const scheduledAt = String((waypoint as Record<string, unknown>).scheduledAt ?? '').trim();
      return calendarDate(scheduledAt.slice(0, 10));
    })
    .find((date): date is string => Boolean(date)) ?? null;
}

export function tripDaysFromPlannerPayload(payload: unknown): AiTripDayContext[] {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) return [];
  const routes = (payload as Record<string, unknown>).routes;
  if (!Array.isArray(routes)) return [];

  return routes.slice(0, MAX_TRIP_DAYS).flatMap((candidate) => {
    if (!candidate || typeof candidate !== 'object' || Array.isArray(candidate)) return [];
    const route = candidate as Record<string, unknown>;
    const id = String(route.id ?? '').trim();
    const name = String(route.name ?? '').replace(/\s+/g, ' ').trim();
    if (!id || !name) return [];
    const hasCanonicalDate = Object.prototype.hasOwnProperty.call(route, 'date');
    const date = hasCanonicalDate ? calendarDate(route.date) : legacyRouteDate(route);
    return [{ id: id.slice(0, 120), name: name.slice(0, 90), date }];
  });
}

function applyAssistantUiEffects(response: AiChatResponse, tripSlug: string): void {
  const mutatedTools = response.toolCalls
    ?.filter((call) => call.mutated && !call.isError)
    .map((call) => call.name) ?? [];
  if (mutatedTools.length > 0) {
    notifyAssistantDataChanged(tripSlug, mutatedTools);
  }
  for (const action of response.uiActions ?? []) {
    if (action.type !== 'focus_map') continue;
    if (!Number.isFinite(action.latitude) || !Number.isFinite(action.longitude)) continue;
    focusAssistantMap(action.latitude, action.longitude, action.zoom ?? 14);
  }
}

export async function askDoddledaysAi({
  token,
  tripSlug,
  message,
  history,
  location,
  locale,
  pathname,
  tripDays = [],
  mapContext,
  fetchImpl = fetch,
}: {
  token: string;
  tripSlug: string;
  message: string;
  history: AiChatMessage[];
  location: AiLocationContext;
  locale: string;
  pathname?: string;
  tripDays?: AiTripDayContext[];
  mapContext?: AssistantMapContext | null;
  fetchImpl?: typeof fetch;
}): Promise<AiChatResponse> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) {
    throw new AiClientError(0, 'offline', { code: 'AI_OFFLINE', stage: 'network', retryable: true });
  }
  const messages = chatMessages(history, message);
  const now = new Date();
  const visibleMap = mapContext === undefined ? currentAssistantMapContext() : mapContext;
  if (history.length === 0) conversationStateByTrip.delete(tripSlug);
  const conversationState = conversationStateByTrip.get(tripSlug);

  let response: Response;
  try {
    response = await fetchImpl(`${aiServiceUrl()}/chat`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        messages,
        context: {
          trip_slug: tripSlug,
          website_language: locale,
          local_time: now.toISOString(),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          path: pathname || (typeof window !== 'undefined' ? window.location.pathname : ''),
          location,
          trip_days: tripDays.slice(0, MAX_TRIP_DAYS),
          ...(visibleMap ? { map: visibleMap } : {}),
          ...(conversationState ? { conversation_state: conversationState } : {}),
        },
      }),
    });
  } catch (error) {
    if (error instanceof AiClientError) throw error;
    const offline = typeof navigator !== 'undefined' && navigator.onLine === false;
    throw new AiClientError(0, offline ? 'offline' : 'Could not reach Doddledays AI', {
      code: offline ? 'AI_OFFLINE' : 'AI_NETWORK',
      stage: 'network',
      retryable: true,
    });
  }

  let payload: AiErrorPayload & Partial<AiChatResponse> = {};
  try {
    payload = await response.json() as typeof payload;
  } catch {
    // A non-JSON proxy response is still distinguished from an application error.
  }
  if (!response.ok) {
    throw new AiClientError(response.status, payload.error || 'AI request failed', {
      code: payload.code || `AI_HTTP_${response.status}`,
      stage: payload.stage || 'service',
      retryable: payload.retryable ?? response.status >= 500,
      requestId: payload.request_id || response.headers.get('X-Doddledays-Request-Id') || undefined,
    });
  }
  if (typeof payload.text !== 'string' || !payload.text.trim()) {
    throw new AiClientError(502, 'AI returned an empty response', {
      code: 'AI_EMPTY_RESPONSE',
      stage: 'model',
      retryable: true,
      requestId: response.headers.get('X-Doddledays-Request-Id') || undefined,
    });
  }
  const result = payload as AiChatResponse;
  if (result.conversationState) {
    conversationStateByTrip.set(tripSlug, result.conversationState);
  }
  applyAssistantUiEffects(result, tripSlug);
  return result;
}

export function currentTripSlug(pathname: string, search: string, storedSlug?: string | null): string | null {
  const projectMatch = pathname.match(/^\/projects\/([^/]+)/);
  if (projectMatch?.[1]) return decodeURIComponent(projectMatch[1]);
  if (pathname === '/memories') {
    const queryTrip = new URLSearchParams(search).get('trip');
    return queryTrip || storedSlug || null;
  }
  return null;
}

